import {useMemo} from 'react';
import {useSelector} from "react-redux";

const baseTheme = {
	dark_border: '#343536',
	dark_field: '#272729',
	dark_svg: '#202124',
	placeholder_gray_2: '#c1c1c1',
	placeholder_blue_1: '#001b2a',
	placeholder_blue_3: '#001723',
	placeholder_red_1: '#ff0000',
	placeholder_link_1: '#00f',
	placeholder_link_2: '#369',
	placeholder_green_1: '#008000',
	placeholder_white_2: '#fbfbfb',
	pink_1: '#ffecef',
	pink_2: '#d7002412',
	white_1: {
		color: '#f1eeee',
	},
	grey_2: {
		color: '#70767b',
	},
	white_2: '#f1eeee',
	blue_1: '#001b2a',
	blue_3: '#001824',
	top: {
		height: '52px',
	},
	sort: {
		height: '48px',
	},
	footer: {
		height: '48px',
	},
};

const variants = {
	light: {
		standard_border: '1px solid #f0f0f0',
		header_menu_border: '#f0f0f0',
		color: {
			name: 'light',
		},
		form: {
			inputBorder: '1px',
		},
		post: {
			primary: '#151b21',
			cardBorderTop: '7px',
			cardBorderBottom: '0px',
		},
		main: {
			color: '#444',
			backgroundColor: '#fff',
		},
		container: {
			backgroundColor: '#f2f2f2',
		},
		sort: {
			color: '#aaa',
			active: '#151b21',
			backgroundColor: '#fff',
			borderBottom: '1px',
		},
		sidebar: {
			color: '#1d1d1d',
			backgroundColor: '#fff',
			secondary: '#eaeaea',
		},
		control: {
			color: '#000',
			backgroundColor: '#f7f7f7',
		},
		label: {
			color: '#444',
		},
		skeleton: {
			color: '#eee',
			highlightColor: '#f5f5f5',
		},
		mainText: {
			color: 'rgb(27, 31, 35)',
		},
	},
	dark: {
		standard_border: 'none',
		header_menu_border: '#002437',
		color: {
			name: 'dark',
		},
		form: {
			inputBorder: '0px',
		},
		post: {
			primary: '#dedede',
			cardBorderTop: '0px',
			cardBorderBottom: '8px',
		},
		main: {
			color: '#fff',
			backgroundColor: '#000a10',
		},
		container: {
			backgroundColor: '#001824',
		},
		sort: {
			color: '#d7d7d7',
			active: '#fff',
			backgroundColor: 'linear-gradient(rgb(0, 27, 42) 0%, rgb(0 22 35) 100%)',
			borderBottom: '0px',
		},
		sidebar: {
			color: '#d7d7d7',
			backgroundColor: '#002031',
			secondary: '#001c2a',
		},
		control: {
			color: '#f1f1f1',
			backgroundColor: '#001824',
		},
		label: {
			color: '#ddd',
		},
		skeleton: {
			color: '#272729',
			highlightColor: '#272729',
		},
		mainText: {
			color: '#fff',
		},
	},
};

function isObject(item) {
	return item && typeof item === 'object' && !Array.isArray(item);
}

function mergeDeep(left, right) {
	if (!isObject(right)) {
		return left || {};
	}
	if (!isObject(left)) {
		return right;
	}
	const result = {};
	for (const [key, value] of Object.entries(left)) {
		if (hasOwnProperty.call(right, key)) {
			result[key] = mergeDeep(value, right[key]);
		} else {
			result[key] = value;
		}
	}
	for (const [key, value] of Object.entries(right)) {
		if (!hasOwnProperty.call(left, key)) {
			result[key] = value;
		}
	}
	return result;
}

const getThemeVariant = (variant, options) => {
	const theme = mergeDeep(baseTheme, variants[variant]);
	Object.assign(theme, options);
	return theme;

}

const useTheme = (variant) => {
	const { fullWidth } = useSelector(({clientSettings}) => clientSettings);
	return useMemo(() => getThemeVariant(variant, { fullWidth }), [variant, fullWidth]);
}

export default useTheme;
